// src/components/ItemSale.js
import React, { memo, useState } from "react";
import { supabase } from "../supabaseClient";

const HOBBLI_STORE_ID = "02a5780e-5171-4577-b4b2-d8780937f223";

const ItemSale = memo(({ card, darkMode = false, onClose }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [contactError, setContactError] = useState("");

  const mainImage = Array.isArray(card.images) && card.images.length > 0 ? card.images[0] : null;

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: "1rem",
  };

  const cardStyle = {
    maxWidth: "500px",
    width: "100%",
    border: darkMode ? "1px solid #444" : "1px solid #ddd",
    borderRadius: "8px",
    overflow: "hidden",
    textAlign: "center",
    background: darkMode ? "#1e1e1e" : "#fff",
    color: darkMode ? "#eee" : "#000",
    boxShadow: darkMode
      ? "0 4px 20px rgba(0,0,0,0.9)"
      : "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    maxHeight: "90vh",
    overflowY: "auto",
  };

  const closeButtonStyle = {
    position: "absolute",
    top: "8px",
    right: "8px",
    background: "transparent",
    border: "none",
    color: darkMode ? "#eee" : "#333",
    fontSize: "1.5rem",
    cursor: "pointer",
  };

  const imageStyle = {
    width: "100%",
    objectFit: "cover",
    background: darkMode ? "#333" : "#f0f0f0",
  };

  const infoStyle = {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const nameStyle = {
    fontWeight: "bold",
    fontSize: "1.1em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const priceStyle = {
    color: darkMode ? "#0af" : "#0070f3",
    fontSize: "1.1em",
    fontWeight: "bold",
  };

  const sendMessage = async () => {
    if (sending) return;
    setContactError("");

    const firstName = document.getElementById("firstName").value.trim();
    const lastName = document.getElementById("lastName").value.trim();
    const email = document.getElementById("email").value.trim();
    const location = document.getElementById("location").value.trim();
    const messageText = document.getElementById("message").value.trim();
    const offerAmount = card.sale?.acceptOffers
      ? document.getElementById("offerAmount")?.value.trim()
      : null;

    if (!firstName || !lastName || !email) {
      setContactError("Please fill in First Name, Last Name, and Email.");
      return;
    }

    setSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const GUEST_USER_ID = "5ce4c73c-9222-477c-8308-4bff6e920370";
      const isPlatformShop = Boolean(card.sale?.isPlatformShop);

      const senderId = isPlatformShop
        ? null
        : session?.user?.id ?? GUEST_USER_ID;

      const receiverId = isPlatformShop
        ? HOBBLI_STORE_ID
        : card.sale?.sellerId;

      if (!receiverId) {
        setContactError("This seller cannot be contacted right now.");
        return;
      }

      const fullMessage = `
A buyer is interested in your item.

ITEM:
- ${card.year ? card.year + " - " : ""}${card.name}
- Set: ${card.set_name}
- Price: $${card.sale.price}

BUYER INFORMATION:
- ${firstName} ${lastName}
- Email: ${email}
- Location: ${location || "Not provided"}

${offerAmount ? `OFFER: $${offerAmount}\n` : ""}
MESSAGE:
${messageText || "(No message)"}
      `.trim();

      const { error } = await supabase.from("messages").insert({
        sender_id: senderId,
        receiver_id: receiverId,
        content: fullMessage,
        metadata: {
          first_name: firstName,
          last_name: lastName,
          email,
          city_state: location,
          offer_amount: offerAmount ? Number(offerAmount) : null,
          listing_id: card.listingId || card.id,
          seller_account_id: card.sale?.sellerAccountId || null,
          shop_id: card.sale?.shopId || null,
          item_id: card.id,
          item_name: card.name,
          set_name: card.set_name,
          year: card.year,
        },
      });

      if (error) throw error;

      onClose();
    } catch (err) {
      console.error("Error sending message:", err);
      setContactError("Could not send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>
          &times;
        </button>

        {showContactForm ? (
          <div style={infoStyle}>
            <h2>{card.sale?.isPlatformShop ? "Contact Hobbli" : "Contact Seller"}</h2>
            {contactError ? <div role="alert" style={{ padding: "9px 11px", borderRadius: "8px", background: "rgba(220,53,69,.10)", color: darkMode ? "#ffb4ab" : "#b42318", fontSize: ".88rem" }}>{contactError}</div> : null}
            <p>
              You are reaching out about:{" "}
              <strong>
                {card.year ? `${card.year} - ` : ""}
                {card.name}
              </strong>
            </p>

            {card.sale?.acceptOffers && (
              <div className="form-group">
                <label>Offer Amount ($)</label>
                <input type="number" id="offerAmount" placeholder="Enter your offer" min={0} />
              </div>
            )}

            <div className="form-group">
              <label>First Name</label>
              <input type="text" id="firstName" />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input type="text" id="lastName" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" id="email" />
            </div>
            <div className="form-group">
              <label>City / State</label>
              <input type="text" id="location" placeholder="e.g., Denver, CO" />
            </div>
            <div className="form-group">
              <label>Message (optional)</label>
              <textarea id="message" rows="4"></textarea>
            </div>

            <p style={{ fontSize: "0.8em", opacity: 0.7 }}>
              * Your name, email, and location will be shared with the seller if a purchase is agreed upon.
            </p>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                marginTop: "8px",
              }}
            >
              <button onClick={sendMessage} className="contact-btn" disabled={sending}>
                {sending ? "Sending..." : "Send Message"}
              </button>
              <button onClick={() => setShowContactForm(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={infoStyle}>
            {mainImage ? (
              <img src={mainImage} alt={card.name || "Unnamed Item"} style={imageStyle} />
            ) : (
              <div
                style={{
                  ...imageStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: darkMode ? "#888" : "#888",
                }}
              >
                No Image
              </div>
            )}

            <div style={nameStyle}>
              {card.year ? `${card.year} - ` : ""}
              {card.name || "Unnamed Item"}
            </div>
            <div style={priceStyle}>${card.sale?.price?.toLocaleString() || "N/A"}</div>

            <button
              style={{
                marginTop: "8px",
                padding: "6px 12px",
                cursor: "pointer",
                borderRadius: "4px",
                border: "1px solid",
                background: darkMode ? "#333" : "#fff",
              }}
              onClick={() => setShowContactForm(true)}
            >
              {card.sale?.isPlatformShop ? "Contact Hobbli" : "Contact Seller"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default ItemSale;
